/**
 * NotificationService - Toast and modal capabilities for FR-035
 * Toast notifications for standard operations, modal dialogs for critical errors
 */

import toast, { type ToastOptions } from 'react-hot-toast';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastOptionsConfig {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  action?: { label: string; callback: () => void };
  closable?: boolean;
}

export interface ModalAction {
  label: string;
  value: string;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
}

export interface ModalConfig {
  title: string;
  message: string;
  severity?: 'info' | 'warning' | 'error' | 'success';
  actions: ModalAction[];
  closable?: boolean;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  details?: string;
}

export interface NotificationServiceConfig {
  defaultToastDuration?: number;
  errorToastDuration?: number;
}

/**
 * NotificationService provides toast and modal capabilities.
 * Toasts use react-hot-toast; modals require NotificationProvider/useNotification for React integration.
 */
class NotificationServiceClass {
  private modalResolver: ((value: string) => void) | null = null;
  private defaultToastDuration = 4000;
  private errorToastDuration = 5000;

  configure(config: NotificationServiceConfig): void {
    if (config.defaultToastDuration !== undefined) {
      this.defaultToastDuration = config.defaultToastDuration;
    }
    if (config.errorToastDuration !== undefined) {
      this.errorToastDuration = config.errorToastDuration;
    }
  }

  /**
   * Show a toast notification for standard operations
   */
  showToast(message: string, type: ToastType = 'info', options: ToastOptionsConfig = {}): string | undefined {
    const duration = type === 'error' ? (options.duration ?? this.errorToastDuration) : (options.duration ?? this.defaultToastDuration);
    
    const toastOptions: ToastOptions = {
      duration: options.closable === false ? Infinity : duration,
      position: options.position ?? 'top-right',
    };

    switch (type) {
      case 'success':
        return toast.success(message, toastOptions);
      case 'error':
        return toast.error(message, {
          ...toastOptions,
          duration: options.closable === false ? Infinity : duration,
        });
      case 'warning':
        return toast(message, { ...toastOptions, icon: '⚠️' });
      case 'loading':
        return toast.loading(message, toastOptions);
      case 'info':
      default:
        return toast(message, toastOptions);
    }
  }

  /**
   * Dismiss a toast by ID
   */
  dismissToast(toastId?: string): void {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  }

  /**
   * Show a critical error modal - requires NotificationProvider to be mounted.
   * Returns a promise that resolves with the action value when user makes a choice.
   */
  showModal(config: ModalConfig): Promise<string> {
    return new Promise((resolve) => {
      this.modalResolver = resolve;
      this.emitModalConfig(config);
    });
  }

  /**
   * Show a confirmation dialog
   */
  async showConfirmation(
    message: string,
    title = 'Confirm',
    options?: { confirmLabel?: string; cancelLabel?: string; danger?: boolean }
  ): Promise<boolean> {
    const result = await this.showModal({
      title,
      message,
      severity: options?.danger ? 'error' : 'warning',
      actions: [
        { label: options?.cancelLabel ?? 'Cancel', value: 'cancel', primary: false },
        { label: options?.confirmLabel ?? 'Confirm', value: 'confirm', primary: true, danger: options?.danger },
      ],
      closable: true,
    });
    return result === 'confirm';
  }

  // Modal event handling - used by NotificationProvider
  private modalConfigListeners: ((config: ModalConfig) => void)[] = [];

  onModalRequest(listener: (config: ModalConfig) => void): () => void {
    this.modalConfigListeners.push(listener);
    return () => {
      this.modalConfigListeners = this.modalConfigListeners.filter((l) => l !== listener);
    };
  }

  private emitModalConfig(config: ModalConfig): void {
    this.modalConfigListeners.forEach((listener) => listener(config));
  }

  resolveModal(value: string): void {
    if (this.modalResolver) {
      this.modalResolver(value);
      this.modalResolver = null;
    }
  }
}

export const NotificationService = new NotificationServiceClass();
